import React, { useState, useEffect } from "react";
import { postReminder } from "../../../services/reminder.services";
import { useDispatch } from "react-redux";
import { fetchReminders } from "../../../state/reducers/reminderSlice";
import toast from "react-hot-toast";

const AddReminderModal = ({ onReminderClose }) => {
  const [reminder, setReminder] = useState({
    description: "",
    trigger_date: "",
    trigger_time: "",
  });
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Set minimum date to today
  const today = new Date().toISOString().split("T")[0];

  // Set focus to the description field on mount
  useEffect(() => {
    document.getElementById("description")?.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReminder({ ...reminder, [name]: value });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!reminder.description.trim()) {
      newErrors.description = "Please enter a description";
    }

    if (!reminder.trigger_date) {
      newErrors.trigger_date = "Please select a date";
    }

    if (!reminder.trigger_time) {
      newErrors.trigger_time = "Please enter a time";
    } else {
      // Validate time format
      const timeMatch = reminder.trigger_time.match(
        /(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/
      );
      if (!timeMatch) {
        newErrors.trigger_time =
          "Please enter a valid time format (e.g., 09:30 AM)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Parse the time value - handle both dropdown and manual input formats
      const timeValue = reminder.trigger_time;
      let hours, minutes, period;

      // Try to match different possible time formats
      const timeMatch = timeValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/);

      if (timeMatch) {
        hours = parseInt(timeMatch[1], 10);
        minutes = timeMatch[2];
        period = timeMatch[3]?.toUpperCase() || "";

        if (period === "PM" && hours < 12) {
          hours += 12;
        } else if (period === "AM" && hours === 12) {
          hours = 0;
        }
      } else {
        throw new Error("Invalid time format");
      }

      const triggerTime = `${hours.toString().padStart(2, "0")}:${minutes}`;

      const reminderData = {
        ...reminder,
        trigger_time: triggerTime,
      };

      await postReminder(reminderData);
      toast.success("Reminder set successfully!");
      onReminderClose();
      dispatch(fetchReminders());
    } catch (error) {
      console.error("Error while adding reminder:", error);
      toast.error("Please enter a valid time in HH:MM AM/PM format.");
      setErrors({
        ...errors,
        trigger_time: "Please enter a valid time format (e.g., 09:30 AM)",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateTimeOptions = () => {
    const options = [];
    const periods = ["AM", "PM"];

    // Start from 9 AM
    for (let period of periods) {
      // For AM, start from 9; for PM, start from 1
      const startHour = period === "AM" ? 9 : 1;
      const endHour = 12;

      for (let hour = startHour; hour <= endHour; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMinute = minute.toString().padStart(2, "0");
          options.push(`${formattedHour}:${formattedMinute} ${period}`);
        }
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4 py-6">
      <div className="bg-white shadow-xl rounded-xl w-full max-w-md relative overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 p-5 border-b border-gray-200 z-10">
          <button
            onClick={onReminderClose}
            className="absolute top-5 right-5 text-white opacity-80 hover:opacity-100 focus:outline-none"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <h2 className="text-2xl font-bold text-white text-center">
            Set a Reminder
          </h2>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto p-6 flex-grow">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <textarea
                name="description"
                id="description"
                value={reminder.description}
                onChange={handleChange}
                placeholder="What do you want to be reminded about?"
                rows="3"
                className={`block w-full border ${
                  errors.description ? "border-red-500" : "border-gray-300"
                } rounded-lg shadow-sm px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.description}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="trigger_date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Date
              </label>
              <input
                type="date"
                name="trigger_date"
                id="trigger_date"
                min={today}
                value={reminder.trigger_date}
                onChange={handleChange}
                className={`block w-full border ${
                  errors.trigger_date ? "border-red-500" : "border-gray-300"
                } rounded-lg shadow-sm px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
              />
              {errors.trigger_date && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.trigger_date}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="trigger_time"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Time
              </label>
              <div className="relative">
                <input
                  type="text"
                  list="timeOptions"
                  id="trigger_time"
                  name="trigger_time"
                  value={reminder.trigger_time}
                  onChange={handleChange}
                  placeholder="Type or select a time (e.g., 09:30 AM)"
                  className={`block w-full border ${
                    errors.trigger_time ? "border-red-500" : "border-gray-300"
                  } rounded-lg shadow-sm pl-4 pr-10 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <datalist id="timeOptions">
                  {timeOptions.map((time) => (
                    <option key={time} value={time} />
                  ))}
                </datalist>
              </div>
              {errors.trigger_time && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.trigger_time}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                You can type any time or select from the dropdown
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 p-5 border-t border-gray-200">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onReminderClose}
              className="w-1/3 py-3 px-4 rounded-lg text-gray-700 font-medium border border-gray-300 hover:bg-gray-100 shadow-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className={`w-2/3 flex items-center justify-center py-3 px-4 rounded-lg text-white font-medium shadow-sm ${
                loading
                  ? "bg-indigo-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Setting Reminder...
                </>
              ) : (
                "Set Reminder"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddReminderModal;
